import Mustache from 'mustache';
import Multiselect from '../../multiselect/multiselect';

/**
 * TestDriveSelection class
 */
class TestDriveSelection {

  /**
   * Defines scope of the TestDrive class instance
   */
  #element;

  /**
   * @property {URLSearchParams} urlParams - URLSearchParams instance for URL parameters
   */
  #urlParams = new URLSearchParams(window.location.search);

  /**
   * @property {object} dom - HTMLElements references
   */
  #dom = {};

  /**
   * @property {object} templates - Mustache templates
   */
  #templates = {};

  /**
   * @property {object} data - Data loaded from the JSON embedded to HTML. Data like Location's preresented brands..
   */
  #data = {
    make: this.#urlParams.get('make'),
    series_model: this.#urlParams.get('series_model'),
    location: this.#urlParams.get('location'),
    fuel_type: this.#urlParams.get('fuel_type'),
  };

  /**
   * @property {object} fetchController - AbortController instance for fetch requests
   */
  #fetchController;

  /**
   * @constructor
   * @param {HTMLElement} element - logic is bind to this element
   */
  constructor(element) {
    this.#element = element;

    this.#findContainerElements();
    this.#findTemplates();
    this.#bindEventListeners();

    // Get models if make is already found in URL parameters
    if (Array.from(this.#dom.makeInputs).some((input) => input.checked && input.value === this.#data.make)) {
      this.#updateSection({ section: 'model', skipScrolling: true }).then(() => {

        // Get locations if model is already found in URL parameters
        if (this.#data.series_model) {
          this.#updateSection({ section: 'location', skipScrolling: true });
        }
      });
    }
  }

  /**
   * Find elements that are controlled by TestDrive instance for later access.
   */
  #findContainerElements() {
    this.#dom = {
      makesContainer: this.#element.querySelector('#test-drive-make-section'),
      modelsContainer: this.#element.querySelector('#test-drive-model-section'),
      locationsContainer: this.#element.querySelector('#test-drive-location-section'),
      makeInputs: this.#element.querySelectorAll('#test-drive-make-section input[name="brands[]"]'),
      loaderContainer: document.querySelector('#test-drive-loader-container'),
      pageHeader: document.querySelector('body > header'), // Used for calculating scroll position
    }
  }

  /**
   * Find Mustache templates from the dom
   */
  #findTemplates() {
    const templateElements = document.querySelectorAll('script.mustache-template');

    Array.from(templateElements).forEach((template) => {
      if (template.dataset.name && template.innerHTML) {
        this.#templates[template.dataset.name] = template.innerHTML;
      }
    });
  }

  /**
   * Bind event listeners
   */
  #bindEventListeners() {
    // Make input event listeners
    if (this.#dom.makeInputs) {
      this.#dom.makeInputs.forEach((input) => {
        input.addEventListener('change', (input) => {
          this.#data = {
            make: input.target.value,
            series_model: null,
            location: null,
            fuel_type: null,
          };

          this.#updateQueryStringParameters();
          this.#updateSection({ section: 'model' });
        });
      });
    }
  }

  /**
   * Bind dynamic element event listeners
   * @param {object} settings - Settings for binding event listeners
   */
  #bindDynamicEventListeners(settings = {}) {
    const modelCards = this.#dom.modelsContainer.querySelectorAll('.card');

    // Remove non-existing element event listeners
    this.#dom.modelButtons?.forEach((button) => button.removeEventListener('click'));

    // Add new event listeners for model buttons
    if (modelCards.length) {
      modelCards.forEach((card) => {
        const button = card.querySelector('.js-test-drive-model-button');

        // Reinit propulsion multiselects
        const propulsionSelect = card.querySelector('select.testDrivePropulsion');
        if (!settings.skipPropulsionReinit) {
          Multiselect.reinit(propulsionSelect);

          // Update propulsion value if card is already selected
          window.multiselects[propulsionSelect.name]?.addCallback({ onValueChange: (data) => {
            if (card.classList.contains('card--selected')) {
              this.#data.fuel_type = data.value[0];
              this.#updateSubmitFormValues({ showErrors: true });
              this.#updateQueryStringParameters();
            }

            // Disable model selection button if propulsion is missing
            button.disabled = !data.value[0];
          }});
        }

        // Add event listener for model button
        button?.addEventListener('click', (event) => {
          event.preventDefault();

          // Update card selection
          modelCards?.forEach((card) => card.classList.remove('card--selected'));
          card.classList.add('card--selected');

          this.#data.series_model = event.target.value;

          // Update fuel type value
          const propulsionSelect = card.querySelector('select.testDrivePropulsion');
          const propulsionValue = window.multiselects[propulsionSelect.name]?.getValue()[0];
          this.#data.fuel_type = propulsionValue || null;

          // No need to update location section if just selecting another model since locations are populated by makes
          if (!this.#element.querySelector('#test-drive-submit-button')) {
            this.#updateSection({ section: 'location' });
          } else {
            this.#updateQueryStringParameters();
            this.#updateSubmitFormValues({ showErrors: true });
            this.#scrollToSection(this.#dom.locationsContainer);
          }
        });
      });
    }

    // Re-initialize multiselect for location select
    const testDriveLocationSelect = this.#element.querySelector('select#testDriveLocation');
    if (!this.#dom.testDriveLocationSelect && testDriveLocationSelect) {
      Multiselect.reinit(testDriveLocationSelect);

      // Set callback for location select on value change
      window.multiselects.testDriveLocation.addCallback({ onValueChange: (data) => {
        this.#data.location = data.value[0];
        this.#updateSubmitFormValues({ showErrors: true });
        this.#updateQueryStringParameters();
      }});
    }
  }

  /**
   * Update submit form values
   * @param {object} settings - Settings for updating submit form values
   */
  #updateSubmitFormValues(settings = {}) {
    const selectedCard = this.#dom.modelsContainer.querySelector('.card--selected');

    // Highlight propulsion selection if missing
    selectedCard?.classList?.toggle('propulsion-value-missing',!this.#data.fuel_type);

    const submitForm = this.#dom.locationsContainer.querySelector('#test-drive-selection-submit-form');
    if (submitForm) {
      // Set form values
      submitForm.make.value = this.#data.make || '';
      submitForm.series_model.value = this.#data.series_model || '';
      submitForm.fuel_type.value = this.#data.fuel_type || '';
      submitForm.location.value = this.#data.location || '';

      const errorMessageElement = submitForm.querySelector('.test-drive-error-message');

      // Clear error message
      errorMessageElement.innerText = '';

      // Set error message if location is missing
      if (!this.#data.location && errorMessageElement && settings.showErrors) {
        errorMessageElement.innerText = 'Toimipistevalinta puuttuu';
      }

      // Set error message if propulsion is missing
      if (!this.#data.fuel_type && errorMessageElement && settings.showErrors) {
        errorMessageElement.innerText = 'Käyttövoimavalinta puuttuu';
      }

      // Enable submit button if all values are set
      const isDisabled = !Object.values(this.#data).every((value) => value);
      submitForm.querySelector('button[type="submit"]').disabled = isDisabled;
    }
  }

  /**
   * Set URL query string parameters
   */
  #updateQueryStringParameters() {
    const newParams = new URLSearchParams(window.location.search);

    // Update URL parameters
    Object.entries(this.#data).forEach(([key, value]) => {
      if (key && value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });

    // Replace URL with new parameters
    window.history.replaceState({}, '', decodeURIComponent(`${window.location.pathname}?${newParams}`));
  }

  /**
   * Scroll to section
   * @param {HTMLElement} section - Section to scroll to
   */
  #scrollToSection(section) {
    // Get header element and its height if in mobile
    const headerElement = this.#dom.pageHeader;
    const headerHeight = getComputedStyle(headerElement).position === 'fixed' ? headerElement.offsetHeight : 0;

    // Set viewport height
    const viewportHeight = window.innerHeight - headerHeight;

    // Get section bounding
    const sectionBounding = section.getBoundingClientRect();

    // Scroll to the middle of the section if it fits in the viewport
    const sectionCenterPosition = sectionBounding.top + (sectionBounding.height / 2) + window.scrollY - (window.innerHeight / 2);

    let sectionElement = sectionCenterPosition;

    // Scroll to the next section if it's not already in the viewport
    if (sectionBounding.height >= viewportHeight) {
      // Use top position if section height is bigger than viewport height
      const sectionTopPosition = sectionBounding.top + window.scrollY - 32;

      // Set scroll position
      sectionElement = sectionTopPosition;
    }

    // Scroll to section
    window.scrollTo({
      top: sectionElement,
      behavior: 'smooth',
    });
  }

  /**
   * Updates section with new data
   * @param {object} settings - Settings for updating section
   * @returns {Promise} - Promise that resolves when section is updated
   */
  async #updateSection(settings = {}) {

    // Clear sections
    if (settings.section === 'model') {
      this.#dom.modelsContainer.classList.add('invisible');
      this.#dom.modelsContainer.innerHTML = '';
      this.#dom.locationsContainer.innerHTML = '';
    } else if (settings.section === 'location') {
      this.#dom.locationsContainer.classList.add('invisible');
      this.#dom.testDriveLocationSelect = null;
      this.#dom.locationsContainer.innerHTML = '';
    }

    // Show loader animation and scroll into view
    this.#dom.loaderContainer.classList.add('show');

    if (!settings.skipScrolling) {
      this.#scrollToSection(this.#dom.loaderContainer);
    }

    const data = await this.#fetchSections({ section: settings.section }); // Fetch data

    // Update model section content
    if (settings.section === 'model') {
      const modelsHTML = Mustache.render(this.#templates.models, { modelsData: data.models.modelsData }, { ...data.models.partials, carCard: this.#templates.carCard });
      this.#dom.modelsContainer.innerHTML = modelsHTML;
    }

    // Update location section content
    if (settings.section === 'location') {
      const locationPartials = {
        multiselect: this.#templates.multiselect,
        selectOptions: this.#templates.selectOptions,
      };

      this.#dom.locationsContainer.innerHTML = Mustache.render(this.#templates.locations, data.locations, locationPartials);
    }

    // Small timeout for opacity change
    setTimeout(() => {
      this.#dom.modelsContainer.classList.remove('invisible');
      this.#dom.locationsContainer.classList.remove('invisible');
    }, 200);

    const bindSettings = {}

    if (settings.section === 'location') {
      // Don't reinitialize propulsion multiselects if location section is updated
      bindSettings.skipPropulsionReinit = true;

      // Set fuel type value if missing
      if (!this.#data.fuel_type) {
        const propulsionSelect = this.#dom.modelsContainer.querySelector('.card--selected select.testDrivePropulsion');
        const propulsionValue = window.multiselects[propulsionSelect.name]?.getValue()[0];
        this.#data.fuel_type = propulsionValue || null;
      }
    }

    this.#bindDynamicEventListeners(bindSettings);
    this.#updateQueryStringParameters();
    this.#updateSubmitFormValues();

    // Set section to scroll to
    if (!settings.skipScrolling) {
      let nextSection = this.#dom.makesContainer;

      if (Object.keys(data.models).length) {
        nextSection = this.#dom.modelsContainer;
      }

      if (Object.keys(data.locations).length) {
        nextSection = this.#dom.locationsContainer;
      }

      this.#scrollToSection(nextSection);
    }

    // Hide loader animation
    this.#dom.loaderContainer.classList.remove('show');

    return this;
  }

  /**
   * Fetches sections from the server with url parameters
   * @param {object} settings - Fetch settings
   * @returns {Promise} - Promise that resolves when fetch is complete
   */
  async #fetchSections(settings = {}) {
    if (this.#fetchController) {
      this.#fetchController.abort();
    }

    this.#fetchController = new AbortController();
    const signal = this.#fetchController.signal;

    const params = new URLSearchParams({
      make: this.#data.make,
      series_model: this.#data.series_model,
      location: this.#data.location,
      fuel_type: this.#data.fuel_type,
      section: settings.section,
    });

    const response = await fetch(`/stage-ajax/test-drive-selection?${params}`, { signal });

    return response.json();
  }

  /**
   * Initializes all TestDrive components on the page.
   */
  static init() {
    const element = document.getElementById('test-drive-selection');

    if (element) {
      new TestDriveSelection(element);
    }
  }
}

export default TestDriveSelection;
